import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Shield, MoreVertical, UserMinus, ShieldPlus, ShieldMinus, Crown as CrownIcon, VolumeX, Volume2 } from 'lucide-react';
import { parseUtcDate } from '../../../utils/parseUtcDate';
import CredibilityChip from '../../../components/Credibility/CredibilityChip';
import { resolveMediaUrl } from '../../../utils/resolveMediaUrl';

const ROLE_RANK = { owner: 3, admin: 2, moderator: 1, member: 0 };

const MemberCard = ({
  member,
  isOwner: isOwnerProp,
  isAdmin: isAdminProp,
  isSelf = false,
  canManage = false,       // true if the *viewer* is owner or admin
  canMute = false,          // true if the *viewer* is owner/admin/moderator
  viewerRole,               // the viewer's own role, for the "can't mute equal/higher role" rank check
  viewerIsOwner = false,   // only the owner can promote/demote/transfer
  onRemove,                // (userId) => void
  onPromote,                // (userId) => void  -- member -> admin
  onDemote,                 // (userId) => void  -- admin -> member
  onTransferOwnership,      // (userId) => void
  onMute,                    // (userId) => void
  onUnmute,                  // (userId) => void
}) => {
  const { t } = useTranslation();
  const userId = member?.user_id ?? member?.id ?? 'unknown';
  const displayName = member?.username || member?.name || t('groups.memberCard.userFallback', { id: userId });
  const first = String(displayName).charAt(0).toUpperCase();
  const profilePicture = member?.profile_picture || member?.user?.profile_picture;

  // Derive role from props or member.role
  const role = member?.role || (isOwnerProp ? 'owner' : isAdminProp ? 'admin' : 'member');
  const isOwner = isOwnerProp ?? role === 'owner';
  const isAdmin = isAdminProp ?? role === 'admin';
  const isMuted = !!member?.is_muted &&
    (!member?.muted_until || parseUtcDate(member.muted_until) > new Date());

  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Can the viewer mute/unmute THIS member specifically -- server also
  // enforces "can't mute an equal-or-higher role" (crud_groupies.mute_member),
  // this just keeps the button from appearing where it would 400.
  const canMuteThisMember = canMute && !isSelf && (ROLE_RANK[role] ?? 0) < (ROLE_RANK[viewerRole] ?? 0);

  // Nothing to manage: no menu at all rather than a dead button.
  const showMenuButton = (canManage && !isSelf && !isOwner) || canMuteThisMember;

  const handle = (fn) => {
    setShowMenu(false);
    fn?.(userId);
  };

  return (
    <div className="bg-cream-100 dark:bg-gray-800/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50 hover:border-cream-900/20 dark:hover:border-gray-600/50 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
              {profilePicture ? (
                <img
                  src={resolveMediaUrl(profilePicture)}
                  alt={`${displayName}'s profile`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to default avatar SVG
                    e.currentTarget.src = "/default-avatar.svg";
                    e.currentTarget.onerror = null; // Prevent infinite loop
                  }}
                />
              ) : (
                // If no profile picture, show first letter
                first || 'U'
              )}
            </div>

            {isOwner && (
              <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-1">
                <Crown size={12} className="text-white" />
              </div>
            )}

            {!isOwner && isAdmin && (
              <div className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-1">
                <Shield size={12} className="text-white" />
              </div>
            )}
          </div>

          <div>
            <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
              {displayName}
              {isSelf && <span className="text-gray-500 dark:text-gray-500 font-normal"> {t('groups.memberCard.you')}</span>}
              {isMuted && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-900/30 px-2 py-0.5 rounded-full">
                  <VolumeX size={11} />
                  {t('groups.memberCard.muted')}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-gray-600 dark:text-gray-400 text-sm capitalize">
                {role === 'owner' ? t('groups.memberCard.groupOwnerRole') : role}
              </p>
              <CredibilityChip cred={member?.credibility} size="xs" showAccuracy={false} />
            </div>
          </div>
        </div>

        {showMenuButton && (
          <div className="relative" ref={menuRef}>
            <button
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-300"
              onClick={() => setShowMenu((v) => !v)}
              aria-label={t('groups.memberCard.manageAria', { displayName })}
              aria-haspopup="menu"
              aria-expanded={showMenu}
            >
              <MoreVertical size={16} />
            </button>

            {showMenu && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-20"
              >
                <div className="p-2">
                  {viewerIsOwner && !isAdmin && (
                    <button
                      onClick={() => handle(onPromote)}
                      className="w-full px-3 py-2 text-left text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center space-x-2"
                    >
                      <ShieldPlus size={16} />
                      <span>{t('groups.memberCard.makeAdmin')}</span>
                    </button>
                  )}
                  {viewerIsOwner && isAdmin && (
                    <button
                      onClick={() => handle(onDemote)}
                      className="w-full px-3 py-2 text-left text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center space-x-2"
                    >
                      <ShieldMinus size={16} />
                      <span>{t('groups.memberCard.removeAdmin')}</span>
                    </button>
                  )}
                  {viewerIsOwner && (
                    <button
                      onClick={() => handle(onTransferOwnership)}
                      className="w-full px-3 py-2 text-left text-amber-700 dark:text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 rounded-lg flex items-center space-x-2"
                    >
                      <CrownIcon size={16} />
                      <span>{t('groups.memberCard.transferOwnership')}</span>
                    </button>
                  )}
                  {canMuteThisMember && (
                    <>
                      <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                      {isMuted ? (
                        <button
                          onClick={() => handle(onUnmute)}
                          className="w-full px-3 py-2 text-left text-green-700 dark:text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg flex items-center space-x-2"
                        >
                          <Volume2 size={16} />
                          <span>{t('groups.memberCard.unmuteMember')}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handle(onMute)}
                          className="w-full px-3 py-2 text-left text-amber-700 dark:text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 rounded-lg flex items-center space-x-2"
                        >
                          <VolumeX size={16} />
                          <span>{t('groups.memberCard.muteMember')}</span>
                        </button>
                      )}
                    </>
                  )}
                  {canManage && !isSelf && !isOwner && (
                    <>
                      <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                      <button
                        onClick={() => handle(onRemove)}
                        className="w-full px-3 py-2 text-left text-red-700 dark:text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg flex items-center space-x-2"
                      >
                        <UserMinus size={16} />
                        <span>{t('groups.memberCard.removeFromGroup')}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberCard;
